<template>
    <b-card no-body header-class="d-flex justify-content-between align-items-start">
        <template v-slot:header>
            <h5>Sitting Details</h5>
            <b-dropdown id="addCourtSittingButton" size="sm" variant="outline-secondary" split right v-on:click="addCourtSitting()" v-if="!disabled && clientId">
                <template v-slot:button-content>
                    <i class="fas fa-plus mr-1"></i>Add
                </template>
                <b-dropdown-item v-on:click="addCourtSitting()">Single Sitting</b-dropdown-item>
                <b-dropdown-item v-on:click="addCourtSittings()">Multiple Sittings</b-dropdown-item>
            </b-dropdown>
        </template>

        <div v-for="courtSitting in courtSittings" :key="courtSitting.courtSittingId" class="p-1">
            <b-button-group size="sm" class="d-flex">
                <b-button key="toggle" :variant="courtSitting.included ? 'outline-info' : 'outline-secondary'" class="text-left flex-grow-1" v-b-toggle="'accordion_'  + courtSitting.courtSittingId">{{ courtSitting.sittingDayDateTimeDisplayText }}</b-button>
                <template v-if="!disabled">
                    <b-button key="edit" variant="outline-secondary" class="flex-grow-0 px-2" title="Edit the sitting" v-on:click="editCourtSitting(courtSitting)" v-if="courtSitting.included && clientId"><i class="fas fa-edit"></i></b-button>
                    <b-button key="exclude" variant="outline-secondary" class="flex-grow-0 px-2" title="Exclude the sitting from the order" v-if="courtSitting.included && clientId" v-on:click="excludeCourtSitting(courtSitting)"><i class="fas fa-eye-slash"></i></b-button>
                    <b-button key="include" variant="outline-info" class="flex-grow-0 px-2" title="Include the sitting in the order" v-if="!courtSitting.included && clientId" v-on:click="includeCourtSitting(courtSitting, null, null, null, null, null, null)"><i class="fas fa-eye"></i></b-button>
                    <b-button key="remove" variant="outline-danger" class="flex-grow-0 px-2" title="Remove the sitting from the matter" v-if="clientId" v-on:click="removeCourtSitting(courtSitting)"><i class="fas fa-times"></i></b-button>
                </template>
            </b-button-group>
            <b-collapse :id="'accordion_' + courtSitting.courtSittingId" :accordion="'accordion_'  + courtSitting.courtSittingId" role="tabpanel" class="pb-2 border border-top-0">
                <div class="p-2 m-0 flex-grow-1">
                    <div><span class="font-weight-bold">{{ $t('Entities.Shared.CourtSittingMatterType') }}:</span>&nbsp;{{ courtSitting.courtSittingMatterTypeName }}</div>
                    <div><span class="font-weight-bold">Location / Room:</span>&nbsp;{{ courtSitting.courtLocationRoomName }}</div>
                    <div><span class="font-weight-bold">Presiding Officer/s:</span>&nbsp;{{ courtSitting.judgesDisplayText }}</div>
                    <div v-if="(courtSitting.modeOfSitting != null)"><span class="font-weight-bold">Sitting Mode:</span>&nbsp; {{ courtSitting.modeOfSittingOptionsDisplayText }}</div>
                    <div v-if="(courtSitting.audioQuality != null)"><span class="font-weight-bold">Audio Quality:</span>&nbsp; {{ courtSitting.audioQualityOptionsDisplayText }}</div>
                    <div v-if="(courtSitting.restricted == true)"><span class="font-weight-bold text-danger">Restricted</span></div>
                    <div v-if="(courtSitting.restricted == false)"><span class="font-weight-bold">Not Restricted</span></div>
                    <div v-if="(courtSitting.mandatoryRequest == true)"><span class="font-weight-bold">Mandatory Request</span></div>
                    <div v-if="courtSitting.included && (courtSitting.clientReference != null)"><span class="font-weight-bold">Client Reference:</span>&nbsp; {{ courtSitting.clientReference }}</div>
                    <div v-if="courtSitting.included && (courtSitting.serviceTypeName != null)"><span class="font-weight-bold">Service Type:</span>&nbsp; {{ courtSitting.serviceTypeName }}</div>
                    <div v-if="courtSitting.included && (courtSitting.serviceDelayName != null)"><span class="font-weight-bold">Turnaround:</span>&nbsp; {{ courtSitting.serviceDelayName }}</div>
                    <div v-if="courtSitting.included && (courtSitting.calculatedDueDateTimeUTC != null)"><span class="font-weight-bold">Calculated Due Date / Time:</span>&nbsp;{{ formatDateTime(courtSitting.calculatedDueDateTimeUTC, 'DD/MM/YYYY h:mm a') }}</div>
                    <div v-if="courtSitting.included && (courtSitting.manuallyEnteredDueDateTimeUTC != null)"><span class="font-weight-bold">Manual Due Date / Time:</span>&nbsp;{{ formatDateTime(courtSitting.manuallyEnteredDueDateTimeUTC, 'DD/MM/YYYY h:mm a') }}</div>
                    <div v-if="courtSitting.included && courtSitting.deliveryContactDisplayNames && courtSitting.deliveryContactDisplayNames != ''"><span class="font-weight-bold">Distribution List:</span>&nbsp; {{ courtSitting.deliveryContactDisplayNames }}</div>
                    <div v-if="courtSitting.included && courtSitting.deliveryContactCcDisplayNames && courtSitting.deliveryContactCcDisplayNames != ''"><span class="font-weight-bold">Distribution CC List:</span>&nbsp; {{ courtSitting.deliveryContactCcDisplayNames }}</div>
                    <div v-if="courtSitting.included && courtSitting.deliveryContactBccDisplayNames && courtSitting.deliveryContactBccDisplayNames != ''"><span class="font-weight-bold">Distribution BCC List:</span>&nbsp; {{ courtSitting.deliveryContactBccDisplayNames }}</div>
                </div>
            </b-collapse>
        </div>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg"
                     centered
                     :title="(currentCourtSitting.courtSittingId == null) ? 'New Sitting' : 'Edit Sitting'"
                     v-model="editCourtSittingDialogShown"
                     v-on:ok.prevent="passes(createOrUpdateCourtSitting)"
                     v-on:hidden="addMoreRequestedSittings = false;"
                     v-on:shown="modalShown"
                     body-class="p-0">
                <b-list-group flush>
                    <b-list-group-item>
                        <b-alert v-model="showErrors" variant="danger" dismissible>
                            <div v-for="(error, index) in currentErrors" :key="index">
                                <p>{{ error }}</p>
                            </div>
                        </b-alert>
                        <b-alert :show="((relatedOrdersText != '')  && editSittingAllowed)"
                                 dismissible
                                 variant="warning">
                            {{ relatedOrdersText }}
                        </b-alert>
                        <b-form-row v-if="displayDueWarning">
                            <div class="alert alert-warning col-12" role="alert">
                                Warning: Manual Due Date Time is a public holiday
                            </div>
                        </b-form-row>
                        <b-form-row>
                            <b-form-group label="Sitting Date"
                                          label-for="sittingDateDisplay"
                                          class="col-md-4"
                                          v-if="(currentCourtSitting.courtSittingId != null)">
                                <b-form-input name="sittingDateDisplay"
                                              disabled
                                              :value="currentCourtSitting.sittingDate">
                                </b-form-input>
                            </b-form-group>
                            <validation-provider ref="multipleSittingDatesValidationProvider"
                                                 rules="required"
                                                 name="Sitting Date Range"
                                                 v-slot="{ valid, errors }"
                                                 slim
                                                 v-if="((currentCourtSitting.courtSittingId == null) && addRange)">
                                <b-form-group label="Sitting Date Range"
                                              label-for="sittingsDateRangePicker"
                                              class="col-md-4">
                                    <ejs-daterangepicker name="sittingsDateRangePicker"
                                                         v-model="newSittingsDateRange"
                                                         format="dd/MM/yyyy"
                                                         @open="resetDateValidationProviders('multiple')">
                                    </ejs-daterangepicker>
                                    <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <validation-provider ref="sittingDateValidationProvider"
                                                 rules="required"
                                                 name="Sitting Date"
                                                 v-slot="{ valid, errors }"
                                                 slim
                                                 v-if="((currentCourtSitting.courtSittingId == null) && !addRange)">
                                <b-form-group label="Sitting Date"
                                              label-for="sittingDate"
                                              class="col-md-4">
                                    <ejs-datepicker name="sittingDate"
                                                    v-model="currentCourtSitting.sittingDate"
                                                    format="d/M/yyyy"
                                                    @open="resetDateValidationProviders('single')">
                                    </ejs-datepicker>
                                    <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <b-form-group label="Sitting Day"
                                          label-for="sittingDayCode"
                                          class="col-md-2"
                                          v-if="!addRange">
                                <b-form-input name="sittingDayCode"
                                              maxlength="10"
                                              v-model="currentCourtSitting.sittingDayCode"
                                              :disabled="!editSittingAllowed">
                                </b-form-input>
                            </b-form-group>
                            <b-form-group label="Start Time"
                                          label-for="startTime"
                                          :class="!addRange ? 'col-md-3' : 'col-md-4'">
                                <b-form-input ref="startTimeInput"
                                              name="startTime"
                                              type="time"
                                              step="1"
                                              v-model="currentCourtSitting.startTime"
                                              :disabled="!editSittingAllowed">
                                </b-form-input>
                            </b-form-group>
                            <b-form-group label="End Time"
                                          label-for="endTime"
                                          :class="!addRange ? 'col-md-3' : 'col-md-4'">
                                <b-form-input name="endTime"
                                              type="time"
                                              step="1"
                                              v-model="currentCourtSitting.endTime"
                                              :disabled="!editSittingAllowed">
                                </b-form-input>
                            </b-form-group>
                        </b-form-row>
                        <b-form-row v-if="addRange">
                            <b-form-checkbox name="excludeWeekends"
                                             v-model="excludeWeekends"
                                             v-if="addRange"
                                             switch>
                                Exclude Weekends
                            </b-form-checkbox>
                        </b-form-row>
                        <b-form-row>
                            <b-form-group label="Court Room"
                                          label-for="courtRoomId"
                                          class="col-md-8">
                                <select2 name="courtRoomId"
                                         placeholder="Select a room"
                                         enable-ajax
                                         allow-clear
                                         in-b-modal
                                         key-property="courtRoomId"
                                         text-property="courtLocationRoomName"
                                         v-model="currentCourtSitting.courtRoomId"
                                         :options="initialCourtRooms"
                                         v-on:search="findCourtRooms"
                                         :disabled="!editSittingAllowed">
                                </select2>
                            </b-form-group>
                            <b-form-group :label="$t('Entities.Shared.CourtSittingMatterType')"
                                          label-for="courtSittingMatterType"
                                          class="col-md-4">
                                <select2 name="courtSittingMatterType"
                                         :placeholder="$t('Vue.Shared.Select2Placeholder.CourtSittingMatterType')"
                                         allow-clear
                                         in-b-modal
                                         key-property="courtSittingMatterTypeId"
                                         text-property="name"
                                         v-model="currentCourtSitting.courtSittingMatterTypeId"
                                         :options="allCourtSittingMatterTypes"
                                         :disabled="!editSittingAllowed">
                                </select2>
                            </b-form-group>
                        </b-form-row>
                        <b-form-row>
                            <b-form-group label="Sitting Mode"
                                          label-for="modeOfSitting"
                                          class="col-md-4">
                                <select2 name="modeOfSitting"
                                         :disabled="!editSittingAllowed"
                                         allow-clear
                                         in-b-modal
                                         placeholder="Select a sitting mode"
                                         v-model="currentCourtSitting.modeOfSitting"
                                         :options="allModeOfSittingOptions">
                                </select2>
                            </b-form-group>
                            <b-form-group label="Audio Quality"
                                          label-for="audioQuality"
                                          class="col-md-4">
                                <select2 name="audioQuality"
                                         allow-clear
                                         in-b-modal
                                         placeholder="Select an audio quality"
                                         v-model="currentCourtSitting.audioQuality"
                                         :options="allAudioQualityOptions"
                                         :disabled="!editSittingAllowed">
                                </select2>
                            </b-form-group>
                            <b-form-group label="Restricted"
                                          label-for="restricted"
                                          class="col-md-4 align-self-end">
                                <b-form-checkbox name="restricted"
                                                 :disabled="!editSittingAllowed"
                                                 v-model="currentCourtSitting.restricted"
                                                 class="mb-2"
                                                 switch>
                                </b-form-checkbox>
                            </b-form-group>
                        </b-form-row>
                        <b-form-row>
                            <validation-provider rules="required" ref="presidingOfficerValidationProvider" name="Presiding Officer/s" v-slot="{ valid, errors }" slim>
                                <b-form-group label="Presiding Officer/s"
                                              label-for="courtSittingAssignedJudgeIds"
                                              class="col-md-8">
                                    <select2 ref="courtSittingAssignedJudgeIds"
                                             name="courtSittingAssignedJudgeIds"
                                             placeholder="Select one or more presiding officer/s"
                                             :options="initialCourtJudges"
                                             enable-ajax
                                             multiple
                                             in-b-modal
                                             key-property="courtJudgeId"
                                             text-property="displayName"
                                             v-model="currentCourtSitting.assignedJudgeIds"
                                             v-on:search="findJudges"
                                             :disabled="!editSittingAllowed"
                                             :state="errors[0] ? false : null">
                                    </select2>
                                    <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <b-form-group label="Mandatory Request"
                                          label-for="mandatoryRequest"
                                          class="col-md-4 align-self-end">
                                <b-form-checkbox name="mandatoryRequest"
                                                 v-model="currentCourtSitting.mandatoryRequest"
                                                 class="mb-2"
                                                 switch
                                                 :disabled="!editSittingAllowed">
                                </b-form-checkbox>
                            </b-form-group>
                        </b-form-row>
                    </b-list-group-item>
                    <b-list-group-item>
                        <b-form-row>
                            <b-form-group label="Client Reference"
                                          label-for="clientReference"
                                          class="col-md-4">
                                <b-form-input name="clientReference"
                                              id="clientReference"
                                              maxlength="500"
                                              v-model="currentCourtSitting.clientReference">
                                </b-form-input>
                            </b-form-group>
                            <validation-provider rules="required" name="Service Type" v-slot="vp" slim>
                                <b-form-group label="Service Type"
                                              label-for="serviceTypeId"
                                              class="col-md-4">
                                    <select2 ref="serviceTypeSelect"
                                             name="serviceTypeId"
                                             allow-clear
                                             in-b-modal
                                             :disabled="(isConfirmedOrder && currentCourtSitting.courtOrderId != null) || (currentSelectableServiceTypes == null)"
                                             v-model="currentCourtSitting.serviceTypeId"
                                             :options="currentSelectableServiceTypes"
                                             key-property="serviceTypeId"
                                             text-property="name"
                                             :placeholder="'Select optional service type'"
                                             v-on:select="serviceTypeSelected"
                                             :state="vp.errors[0] ? false : null">
                                    </select2>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                            <validation-provider rules="required" name="Turnaround" v-slot="vp" slim v-if="currentCourtSitting.serviceTypeRequiresServiceDelay">
                                <b-form-group label="Turnaround"
                                              label-for="currentCourtSitting.serviceDelayId"
                                              class="col-md-4">
                                    <select2 ref="serviceDelaySelect"
                                             name="serviceDelayId"
                                             allow-clear
                                             in-b-modal
                                             :disabled="(isConfirmedOrder && currentCourtSitting.courtOrderId != null) || (currentSelectableServiceDelays == null)"
                                             v-model="currentCourtSitting.serviceDelayId"
                                             :options="currentSelectableServiceDelays"
                                             key-property="serviceDelayId"
                                             text-property="name"
                                             placeholder="Select a turnaround"
                                             :state="vp.errors[0] ? false : null">
                                    </select2>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </b-form-row>
                        <b-form-row v-if="currentCourtSitting.calculatedDueDateTimeUTC">
                            <b-form-group label="Calculated Due Date / Time"
                                          label-for="calculatedDueDateTimeUTC"
                                          class="col-md-4">
                                <b-form-input name="calculatedDueDateTimeUTC"
                                              disabled
                                              :value="formatDateTime(currentCourtSitting.calculatedDueDateTimeUTC, 'DD/MM/YYYY h:mm a')">
                                </b-form-input>
                            </b-form-group>
                            <b-form-group label="Manual Due Date / Time"
                                          label-for="manuallyEnteredDueDateTimeUTC"
                                          class="col-md-4">
                                <ejs-datetimepicker name="manuallyEnteredDueDateTimeUTC"
                                                    v-model="currentCourtSitting.manuallyEnteredDueDateTimeUTC"
                                                    format="d/M/yyyy h:mma"
                                                    v-on:change="onManualDueDateChange"></ejs-datetimepicker>
                            </b-form-group>
                        </b-form-row>
                        <b-form-row v-if="!hideDistributionList && !currentCourtSitting.serviceTypeHideDistributionList">
                            <validation-provider ref="distributionListValidationProvider" rules="required" name="Distribution List" v-slot="vp" slim>
                                <b-form-group label="Distribution List"
                                              label-for="deliveryContactIds"
                                              class="col">
                                    <contact-select ref="deliveryContactSelect"
                                                    name="deliveryContactIds"
                                                    :api-url="$urlHelper.getApiUrl('contact', 'delivery')"
                                                    :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                                    :client-id="clientId"
                                                    v-model="currentCourtSitting.deliveryContactIds"
                                                    multiple
                                                    :initial-contacts="initialDeliveryContacts"
                                                    :state="vp.errors[0] ? false : null">
                                    </contact-select>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </b-form-row>
                        <b-form-row v-if="!hideDistributionList && !currentCourtSitting.serviceTypeHideDistributionList">
                            <validation-provider rules="" name="CC List" v-slot="vp" slim>
                                <b-form-group label="Distribution CC List"
                                              label-for="deliveryContactCcIds"
                                              class="col">
                                    <contact-select ref="deliveryContactCcSelect"
                                                    name="deliveryContactCcIds"
                                                    :api-url="$urlHelper.getApiUrl('contact', 'delivery')"
                                                    :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                                    :client-id="clientId"
                                                    v-model="currentCourtSitting.deliveryContactCcIds"
                                                    multiple
                                                    :initial-contacts="initialCcDeliveryContacts"
                                                    :state="vp.errors[0] ? false : null">
                                    </contact-select>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </b-form-row>
                        <b-form-row v-if="!hideDistributionList && !currentCourtSitting.serviceTypeHideDistributionList">
                            <validation-provider rules="" name="BCC List" v-slot="vp" slim>
                                <b-form-group label="Distribution BCC List"
                                              label-for="deliveryContactBccIds"
                                              class="col">
                                    <contact-select ref="deliveryContactBccSelect"
                                                    name="deliveryContactBccIds"
                                                    :api-url="$urlHelper.getApiUrl('contact', 'delivery')"
                                                    :ref-data-api-controller-url="$urlHelper.getApiUrl('referencedata')"
                                                    :client-id="clientId"
                                                    v-model="currentCourtSitting.deliveryContactBccIds"
                                                    multiple
                                                    :initial-contacts="initialBccDeliveryContacts"
                                                    :state="vp.errors[0] ? false : null">
                                    </contact-select>
                                    <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </b-form-row>
                    </b-list-group-item>
                </b-list-group>
            </b-modal>
        </validation-observer>
    </b-card>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import { DatePickerPlugin, DateRangePickerPlugin, DateTimePickerPlugin } from '@syncfusion/ej2-vue-calendars';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required, required_if } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('required_if', required_if);
    localize('en', en);

    import DateTimeMixin from '../mixins/DateTimeMixin';
    import ErrorsMixin from '../mixins/ErrorsMixin';
    import Select2 from './Select2';

    Vue.use(BootstrapVue);
    Vue.use(DatePickerPlugin);
    Vue.use(DateRangePickerPlugin);
    Vue.use(DateTimePickerPlugin);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('select2', Select2);

    export default {
        name: 'CourtSittings',
        mixins: [DateTimeMixin, ErrorsMixin],
        model: {
            event: 'change',
            prop: 'courtSittingOrderAssignments'
        },
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            courtRegistryId: {
                type: String,
                required: true
            },
            courtMatterId: {
                type: String,
                required: true
            },
            clientId: {
                type: String,
                required: true
            },
            orderId: {
                type: String,
                required: true
            },
            requestedCourtSittings: {
                type: Array,
                default: null
            },
            allCourtSittingMatterTypes: {
                type: Array,
                required: true
            },
            courtSittingOrderAssignments: {
                type: Array,
                default: null
            },
            disabled: {
                type: Boolean,
                default: false
            },
            selectableServiceTypes: {
                type: Array,
                default: null
            },
            serviceTypesNullSelectPlaceholder: {
                type: String,
                default: null
            },
            selectableServiceDelays: {
                type: Array,
                default: null
            },
            allModeOfSittingOptions: {
                type: Array,
                default: null
            },
            allAudioQualityOptions: {
                type: Array,
                default: null
            },
            isConfirmedOrder: {
                type: Boolean,
                default: false
            },
            orderServiceTypeRequiresServiceDelay: {
                type: Boolean,
                default: false
            },
            hideDistributionList: {
                type: Boolean,
                default: false
            },
            initialServiceTypeId: {
                type: String,
                required: false
            },
            initialServiceDelayId: {
                type: String,
                required: false
            },
        },
        data: function () {
            return {
                courtSittings: null,
                currentCourtSittingOrderAssignments: this.courtSittingOrderAssignments,
                currentCourtSitting: {
                    courtSittingId: null,
                    courtSittingMatterTypeId: null,
                    courtRoomId: null,
                    sittingDayCode: null,
                    sittingDate: null,
                    startTime: null,
                    endTime: null,
                    clientReference: null,
                    serviceTypeId: null,
                    serviceDelayId: null,
                    modeOfSitting: null,
                    restricted: false,
                    mandatoryRequest: false,
                    audioQuality: null,
                },
                relatedOrdersText: '',
                editSittingAllowed: true,
                addRange: false,
                newSittingsDateRange: null,
                currentErrors: [],
                initialCourtRooms: null,
                initialCourtJudges: null,
                initialDeliveryContacts: null,
                initialCcDeliveryContacts: null,
                initialBccDeliveryContacts: null,
                editCourtSittingDialogShown: false,
                clientReference: this.clientReference,
                currentSelectableServiceTypes: this.selectableServiceTypes,
                currentSelectableServiceDelays: this.selectableServiceDelays,
                serviceTypeId: null,
                serviceDelayId: null,
                displayDueWarning: false,
                addMoreRequestedSittings: false,
                excludeWeekends: true,
            };
        },
        watch: {
            courtMatterId: function () {
                this.reloadCourtSittings(true);
            },
            selectableServiceTypes: function () {
                this.currentSelectableServiceTypes = this.selectableServiceTypes;
                Vue.nextTick().then(() => {
                    this.$refs.serviceTypeSelect.refresh();
                });
            },
            selectableServiceDelays: function () {
                if (this.currentCourtSitting && this.currentCourtSitting.serviceTypeRequiresServiceDelay) {
                    this.currentSelectableServiceDelays = this.selectableServiceDelays;
                    Vue.nextTick().then(() => {
                        this.$refs.serviceDelaySelect.refresh();
                    });
                }
            },
        },
        computed: {
            showErrors: function () {
                return this.currentErrors.length > 0;
            },
        },
        methods: {
            resetDateValidationProviders: function (typeOfDateSelection) {
                if (typeOfDateSelection == 'single') this.$refs.sittingDateValidationProvider.reset();
                if (typeOfDateSelection == 'multiple') this.$refs.multipleSittingDatesValidationProvider.reset();
            },
            checkRelatedOrders: function () {
                var countRelatedOrders = 0;

                if (this.relatedOrders) {
                    this.relatedOrders.forEach(relatedOrder => {
                        if (relatedOrder.orderId != this.orderId) {
                            countRelatedOrders++;

                            if (!relatedOrder.sittingModificationAllowed)
                                this.editSittingAllowed = false;
                        }
                    });
                }

                var allRelatedOrders = '';
                if (countRelatedOrders > 0)
                    allRelatedOrders = 'Warning: Any changes to these sitting details will affect ' + countRelatedOrders + ' other order';
                if (countRelatedOrders > 1)
                    allRelatedOrders += 's';

                this.relatedOrdersText = allRelatedOrders;
            },
            loadRelatedOrders: function () {
                this.relatedOrdersText = '';
                if (this.currentCourtSitting) {
                    if (this.currentCourtSitting.courtSittingId) {

                        axios
                            .get(this.$urlHelper.getApiUrl('courtsitting', 'relatedorders', [this.currentCourtSitting.courtSittingId]))
                            .then(response => {
                                this.relatedOrders = response.data;
                                this.checkRelatedOrders();
                            })
                            .catch(ex => {
                                var currentErrors = this.handleAxiosException(ex);
                                this.showErrorsToast(currentErrors);
                            });
                    }
                }

            },
            reloadCourtSittings: function (resetIncludedSittings = false) {
                this.currentErrors = [];
                if (this.courtMatterId) {
                    axios
                        .get(this.apiBaseUrl + '/courtsitting/list/' + this.courtMatterId)
                        .then(response => {
                            var courtSittingOrderAssignments = this.currentCourtSittingOrderAssignments;
                            if (courtSittingOrderAssignments == null) courtSittingOrderAssignments = [];
                            this.courtSittings = $.map(response.data, obj => {
                                if (resetIncludedSittings && !courtSittingOrderAssignments.find(element => element.courtSittingId == obj.courtSittingId)) {
                                    var includeSitting = true;
                                    if (this.requestedCourtSittings != null) {
                                        if (!this.requestedCourtSittings.some(s => s.sittingDate == obj.sittingDate)) includeSitting = false;
                                    }
                                    if (includeSitting) {
                                        courtSittingOrderAssignments.push({
                                            courtSittingId: obj.courtSittingId,
                                            courtOrderId: null,
                                            sittingDate: this.formatDateTime(obj.sittingDate, 'DD/MM/YYYY', true, true),
                                            sittingDayCode: obj.sittingDayCode,
                                            sittingDayDateTimeDisplayText: obj.sittingDayDateTimeDisplayText,
                                            calculatedDueDateTimeUTC: null,
                                            manuallyEnteredDueDateTimeUTC: null,
                                            clientReference: null,
                                            serviceTypeId: null,
                                            serviceTypeRequiresServiceDelay: false,
                                            serviceTypeHideDistributionList: false,
                                            serviceDelayId: null,
                                            allDeliveryContactDetails: null,
                                            deliveryContactIds: null,
                                            deliveryContactCcIds: null,
                                            deliveryContactBccIds: null,
                                        });
                                    }
                                }
                                var courtSittingOrderAssignment = courtSittingOrderAssignments.find(element => element.courtSittingId == obj.courtSittingId);
                                if (courtSittingOrderAssignment != null) {
                                    obj.included = true;
                                    if (courtSittingOrderAssignment.courtOrderId) obj.courtOrderId = courtSittingOrderAssignment.courtOrderId;
                                    if (courtSittingOrderAssignment.calculatedDueDateTimeUTC == null) obj.calculatedDueDateTimeUTC = null;
                                    else obj.calculatedDueDateTimeUTC = this.parseDateTime(courtSittingOrderAssignment.calculatedDueDateTimeUTC);
                                    if (courtSittingOrderAssignment.manuallyEnteredDueDateTimeUTC == null) obj.manuallyEnteredDueDateTimeUTC = null;
                                    else obj.manuallyEnteredDueDateTimeUTC = this.parseDateTime(courtSittingOrderAssignment.manuallyEnteredDueDateTimeUTC);
                                    if (courtSittingOrderAssignment.clientReference == null) obj.clientReference = null;
                                    else obj.clientReference = courtSittingOrderAssignment.clientReference;
                                    if (courtSittingOrderAssignment.serviceTypeId == null) {
                                        obj.serviceTypeId = null;
                                        courtSittingOrderAssignment.serviceTypeRequiresServiceDelay = false;
                                        courtSittingOrderAssignment.serviceTypeHideDistributionList = false;
                                    }
                                    else {
                                        obj.serviceTypeId = courtSittingOrderAssignment.serviceTypeId;
                                        var serviceType = this.currentSelectableServiceTypes.find(element => element.serviceTypeId == obj.serviceTypeId);
                                        if (serviceType != null) {
                                            obj.serviceTypeName = serviceType.name;
                                            obj.serviceTypeRequiresServiceDelay = serviceType.requiresServiceDelay;
                                            obj.serviceTypeHideDistributionList = serviceType.hideDistributionList;
                                            courtSittingOrderAssignment.serviceTypeRequiresServiceDelay = serviceType.requiresServiceDelay;
                                            courtSittingOrderAssignment.serviceTypeHideDistributionList = serviceType.hideDistributionList;
                                        } else if (courtSittingOrderAssignment.serviceType) {
                                            obj.serviceTypeName = courtSittingOrderAssignment.serviceType.name;
                                            obj.serviceTypeRequiresServiceDelay = courtSittingOrderAssignment.serviceType.requiresServiceDelay;
                                            obj.serviceTypeHideDistributionList = courtSittingOrderAssignment.serviceType.hideDistributionList;
                                            courtSittingOrderAssignment.serviceTypeRequiresServiceDelay = courtSittingOrderAssignment.serviceType.requiresServiceDelay;
                                            courtSittingOrderAssignment.serviceTypeHideDistributionList = courtSittingOrderAssignment.serviceType.hideDistributionList;
                                        } else {
                                            obj.serviceTypeName = null;
                                            obj.serviceTypeRequiresServiceDelay = false;
                                            obj.serviceTypeHideDistributionList = false;
                                            courtSittingOrderAssignment.serviceTypeRequiresServiceDelay = false;
                                            courtSittingOrderAssignment.serviceTypeHideDistributionList = false;
                                        }
                                    }
                                    if (courtSittingOrderAssignment.serviceDelayId == null) obj.serviceDelayId = null;
                                    else {
                                        obj.serviceDelayId = courtSittingOrderAssignment.serviceDelayId;
                                        var serviceDelay = this.currentSelectableServiceDelays.find(element => element.serviceDelayId == obj.serviceDelayId);
                                        if (serviceDelay != null) {
                                            obj.serviceDelayName = serviceDelay.name;
                                        }
                                    }
                                    obj.allDeliveryContactDetails = courtSittingOrderAssignment.allDeliveryContactDetails;
                                    obj.deliveryContactIds = courtSittingOrderAssignment.deliveryContactIds;
                                    obj.deliveryContactDisplayNames = this.generateContactDisplayNames(obj.deliveryContactIds, obj.allDeliveryContactDetails);
                                    obj.deliveryContactCcIds = courtSittingOrderAssignment.deliveryContactCcIds;
                                    obj.deliveryContactCcDisplayNames = this.generateContactDisplayNames(obj.deliveryContactCcIds, obj.allDeliveryContactDetails);
                                    obj.deliveryContactBccIds = courtSittingOrderAssignment.deliveryContactBccIds;
                                    obj.deliveryContactBccDisplayNames = this.generateContactDisplayNames(obj.deliveryContactBccIds, obj.allDeliveryContactDetails);
                                } else {
                                    obj.included = false;
                                }
                                return obj;
                            });
                            this.currentCourtSittingOrderAssignments = [];
                            var listChanged = false;
                            courtSittingOrderAssignments.forEach(obj => {
                                var courtSitting = this.courtSittings.find(element => element.courtSittingId == obj.courtSittingId);
                                if (courtSitting != null) {
                                    this.currentCourtSittingOrderAssignments.push(obj);
                                    if (obj.sittingDayCode != courtSitting.sittingDayCode) {
                                        obj.sittingDayCode = courtSitting.sittingDayCode;
                                        listChanged = true;
                                    }
                                } else {
                                    listChanged = true;
                                }
                            });
                            if (listChanged || resetIncludedSittings) {
                                this.$emit('change', this.currentCourtSittingOrderAssignments);
                            }
                        })
                        .catch(ex => {
                            this.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.currentErrors.length != 0) {
                                this.showErrorsToast(this.currentErrors);
                            } else if (resetIncludedSittings) {
                                this.addRequestedCourtSittings();
                            }
                        });
                }
            },
            generateContactDisplayNames: function (contactIds, allContacts) {
                var contactDisplayNames = '';
                if (contactIds && allContacts) {
                    contactIds.forEach(contactId => {
                        var contactName = allContacts.find(e => e.contactId == contactId)?.name;
                        if (contactName) {
                            if (contactDisplayNames != '') contactDisplayNames += ', ';
                            contactDisplayNames += contactName;
                        }
                    });
                }
                return contactDisplayNames;
            },
            addRequestedCourtSittings() {
                if (this.requestedCourtSittings) {
                    var missingCourtSitting = this.requestedCourtSittings.find(s =>
                        (this.currentCourtSittingOrderAssignments == null) ||
                        (!this.currentCourtSittingOrderAssignments.some(s1 => this.sittingDateMatches(s1, s))));
                    if (missingCourtSitting) {
                        this.addMoreRequestedSittings = true;
                        this.addCourtSitting(missingCourtSitting);
                    } else {
                        this.addMoreRequestedSittings = false;
                    }
                }
            },
            sittingDateMatches: function (courtSittingOrderAssignment, requestedCourtSitting) {
                var courtSittingOrderAssignmentSittingDate = this.parseDateTime(courtSittingOrderAssignment.sittingDate, ['YYYY-MM-DD hh:mm:ss', 'YYYY-MM-DD', 'DD/MM/YYYY hh:mm:ss', 'DD/MM/YYYY'], true, true);
                var courtSittingOrderAssignmentSittingDateString = this.formatDateTime(courtSittingOrderAssignmentSittingDate, 'YYYY-MM-DD', true, true);
                var requestedCourtSittingDate = this.parseDateTime(requestedCourtSitting.sittingDate, ['YYYY-MM-DD hh:mm:ss','YYYY-MM-DD','DD/MM/YYYY hh:mm:ss','DD/MM/YYYY'], true, true);
                var requestedCourtSittingDateString = this.formatDateTime(requestedCourtSittingDate, 'YYYY-MM-DD', true, true);
                return courtSittingOrderAssignmentSittingDateString == requestedCourtSittingDateString;
            },
            addCourtSitting: function (requestedCourtSitting = null) {
                this.editSittingAllowed = true;
                this.currentCourtSitting = {
                    courtSittingId: null,
                    courtSittingMatterTypeId: requestedCourtSitting ? requestedCourtSitting.courtSittingMatterTypeId : null,
                    courtRoomId: requestedCourtSitting ? requestedCourtSitting.courtRoomId : null,
                    assignedJudgeIds: requestedCourtSitting ? requestedCourtSitting.assignedJudgeIds : null,
                    sittingDayCode: null,
                    sittingDate: requestedCourtSitting ? this.parseDateTime(requestedCourtSitting.sittingDate) : null,
                    startTime: requestedCourtSitting ? this.formatDateTime(requestedCourtSitting.startTime, 'HH:mm:ss', true, true) : null,
                    endTime: requestedCourtSitting ? this.formatDateTime(requestedCourtSitting.endTime, 'HH:mm:ss', true, true) : null,
                    clientReference: null,
                    serviceTypeId: this.initialServiceTypeId != null ? this.initialServiceTypeId : null,
                    serviceDelayId: this.initialServiceDelayId != null ? this.initialServiceDelayId : null,

                    calculatedDueDateTimeUTC: null,
                    manuallyEnteredDueDateTimeUTC: null,
                    modeOfSitting: null,
                    restricted: false,
                    mandatoryRequest: false,
                    audioQuality: null,
                    deliveryContactIds: requestedCourtSitting && requestedCourtSitting.deliveryContactIds ? requestedCourtSitting.deliveryContactIds : null,
                    deliveryContactCcIds: requestedCourtSitting && requestedCourtSitting.deliveryContactCcIds ? requestedCourtSitting.deliveryContactCcIds : null,
                    deliveryContactBccIds: requestedCourtSitting && requestedCourtSitting.deliveryContactBccIds ? requestedCourtSitting.deliveryContactBccIds : null,
                };

                this.initialCourtRooms = null;
                this.initialCourtJudges = null;
                this.initialDeliveryContacts = null;
                this.initialCcDeliveryContacts = null;
                this.initialBccDeliveryContacts = null;
                this.relatedOrdersText = '';

                
                if (requestedCourtSitting == null && this.initialServiceTypeId != null) {
                    this.currentCourtSitting.serviceTypeId = this.initialServiceTypeId;

                    var foundServiceType = this.currentSelectableServiceTypes.find(serviceType => serviceType.serviceTypeId === this.currentCourtSitting.serviceTypeId);
                    if (foundServiceType != null) {
                        this.currentCourtSitting.serviceTypeRequiresServiceDelay = foundServiceType.requiresServiceDelay;
                        this.currentCourtSitting.serviceTypeHideDistributionList = foundServiceType.hideDistributionList;
                    }

                    this.currentCourtSitting.serviceDelayId = this.initialServiceDelayId;
                }

                var i;
                if (requestedCourtSitting != null) {
                    if (requestedCourtSitting.courtRoomId != null) this.initialCourtRooms = [{ id: requestedCourtSitting.courtRoomId, text: requestedCourtSitting.courtLocationRoomName }];
                    if (requestedCourtSitting.assignedJudgeIds != null) {
                        this.initialCourtJudges = [];
                        for (i = 0; i < requestedCourtSitting.assignedJudgeIds.length; i++) {
                            this.initialCourtJudges.push({
                                courtJudgeId: requestedCourtSitting.assignedJudgeIds[i],
                                displayName: requestedCourtSitting.assignedJudgeDisplayNames[i]
                            })
                        }
                    }
                    if (requestedCourtSitting.deliveryContactIds != null) {
                        this.initialDeliveryContacts = [];
                        for (i = 0; i < requestedCourtSitting.deliveryContactIds.length; i++) {
                            this.initialDeliveryContacts.push({
                                contactId: requestedCourtSitting.deliveryContactIds[i],
                                name: requestedCourtSitting.groupedDeliveryContactNames.To[i]
                            });
                        }
                    }
                    if (requestedCourtSitting.deliveryContactCcIds != null) {
                        this.initialCcDeliveryContacts = [];
                        for (i = 0; i < requestedCourtSitting.deliveryContactCcIds.length; i++) {
                            this.initialCcDeliveryContacts.push({
                                contactId: requestedCourtSitting.deliveryContactCcIds[i],
                                name: requestedCourtSitting.groupedDeliveryContactNames.Cc[i]
                            });
                        }
                    }
                    if (requestedCourtSitting.deliveryContactBccIds != null) {
                        this.initialBccDeliveryContacts = [];
                        for (i = 0; i < requestedCourtSitting.deliveryContactBccIds.length; i++) {
                            this.initialBccDeliveryContacts.push({
                                contactId: requestedCourtSitting.deliveryContactBccIds[i],
                                name: requestedCourtSitting.groupedDeliveryContactNames.Bcc[i]
                            });
                        }
                    }

                    if (requestedCourtSitting.serviceTypeId == null && this.initialServiceTypeId != null) {
                        this.currentCourtSitting.serviceTypeId = this.initialServiceTypeId;

                        var serviceType = this.currentSelectableServiceTypes.find(serviceType => serviceType.serviceTypeId === this.currentCourtSitting.serviceTypeId);
                        if (serviceType != null) {
                            this.currentCourtSitting.serviceTypeRequiresServiceDelay = serviceType.requiresServiceDelay;
                            this.currentCourtSitting.serviceTypeHideDistributionList = serviceType.hideDistributionList;
                        }

                        this.currentCourtSitting.serviceDelayId = this.initialServiceDelayId;
                    }
                    else {
                        this.currentCourtSitting.serviceTypeId = requestedCourtSitting.serviceTypeId;
                        this.currentCourtSitting.serviceTypeRequiresServiceDelay = requestedCourtSitting.serviceType != null ? requestedCourtSitting.serviceType.requiresServiceDelay : false;
                        this.currentCourtSitting.serviceTypeHideDistributionList = requestedCourtSitting.serviceType != null ? requestedCourtSitting.serviceType.hideDistributionList : false;
                        this.currentCourtSitting.serviceDelayId = requestedCourtSitting.serviceDelayId;
                    }                    
                } else if (this.currentCourtSittingOrderAssignments != null && this.currentCourtSittingOrderAssignments.length > 0) {
                    var assignedSittingIds = this.currentCourtSittingOrderAssignments.map(e => e.courtSittingId);
                    var previousCourtSitting = this.courtSittings.findLast(e => assignedSittingIds.includes(e.courtSittingId));
                    this.currentCourtSitting.serviceTypeId = previousCourtSitting.serviceTypeId;
                    this.currentCourtSitting.serviceDelayId = previousCourtSitting.serviceDelayId;
                    this.currentCourtSitting.assignedJudgeIds = previousCourtSitting.assignedJudgeIds;
                    this.currentCourtSitting.deliveryContactIds = previousCourtSitting.deliveryContactIds;
                    this.currentCourtSitting.deliveryContactCcIds = previousCourtSitting.deliveryContactCcIds;
                    this.currentCourtSitting.deliveryContactBccIds = previousCourtSitting.deliveryContactBccIds;
                    if (this.currentCourtSitting.serviceTypeId != null) {
                        var obj = this.currentSelectableServiceTypes.find(serviceType => serviceType.serviceTypeId === this.currentCourtSitting.serviceTypeId);
                        if (obj != null) {
                            this.currentCourtSitting.serviceTypeRequiresServiceDelay = obj.requiresServiceDelay;
                            this.currentCourtSitting.serviceTypeHideDistributionList = obj.hideDistributionList;
                        }
                    }
                    if (previousCourtSitting.assignedJudgeIds != null) {
                        this.initialCourtJudges = [];
                        for (i = 0; i < previousCourtSitting.assignedJudgeIds.length; i++) {
                            this.initialCourtJudges.push({
                                courtJudgeId: previousCourtSitting.assignedJudgeIds[i],
                                displayName: previousCourtSitting.assignedJudgeDisplayNames[i]
                            })
                        }
                    }
                    this.initialDeliveryContacts = previousCourtSitting.allDeliveryContactDetails;
                    this.initialCcDeliveryContacts = previousCourtSitting.allDeliveryContactDetails;
                    this.initialBccDeliveryContacts = previousCourtSitting.allDeliveryContactDetails;
                }
                this.newSittingsDateRange = null;
                this.addRange = false;
                this.currentErrors = [];
                this.editCourtSittingDialogShown = true;
                Vue.nextTick().then(() => {
                    this.$refs.sittingDateValidationProvider.reset();
                    this.$refs.presidingOfficerValidationProvider.reset();
                    this.$refs.deliveryContactSelect.reset();
                    this.$refs.deliveryContactCcSelect.reset();
                    this.$refs.deliveryContactBccSelect.reset();
                    this.$refs.distributionListValidationProvider.reset();
                });
            },
            addCourtSittings: function () {
                this.editSittingAllowed = true;
                this.currentCourtSitting = {
                    courtSittingId: null,
                    courtSittingMatterTypeId: null,
                    courtRoomId: null,
                    assignedJudgeIds: null,
                    sittingDayCode: null,
                    sittingDate: null,
                    startTime: null,
                    endTime: null,
                    clientReference: null,
                    serviceTypeId: null,
                    serviceDelayId: null,
                    calculatedDueDateTimeUTC: null,
                    manuallyEnteredDueDateTimeUTC: null,
                    modeOfSitting: null,
                    restricted: false,
                    mandatoryRequest: false,
                    audioQuality: null,
                    deliveryContactIds: null,
                    deliveryContactCcIds: null,
                    deliveryContactBccIds: null,
                };
                this.initialCourtRooms = null;
                this.initialCourtJudges = null;
                this.initialDeliveryContacts = null;
                this.initialCcDeliveryContacts = null;
                this.initialBccDeliveryContacts = null;
                this.relatedOrdersText = '';
                if (this.currentCourtSittingOrderAssignments && this.currentCourtSittingOrderAssignments.length) {
                    var assignedSittingIds = this.currentCourtSittingOrderAssignments.map(e => e.courtSittingId);
                    var previousCourtSitting = this.courtSittings.findLast(e => assignedSittingIds.includes(e.courtSittingId));
                    this.currentCourtSitting.serviceTypeId = previousCourtSitting.serviceTypeId;
                    this.currentCourtSitting.serviceDelayId = previousCourtSitting.serviceDelayId;
                    this.currentCourtSitting.assignedJudgeIds = previousCourtSitting.assignedJudgeIds;
                    this.currentCourtSitting.deliveryContactIds = previousCourtSitting.deliveryContactIds;
                    this.currentCourtSitting.deliveryContactCcIds = previousCourtSitting.deliveryContactCcIds;
                    this.currentCourtSitting.deliveryContactBccIds = previousCourtSitting.deliveryContactBccIds;
                    if (this.currentCourtSitting.serviceTypeId != null) {
                        var obj = this.currentSelectableServiceTypes.find(serviceType => serviceType.serviceTypeId === this.currentCourtSitting.serviceTypeId);
                        if (obj != null) {
                            this.currentCourtSitting.serviceTypeRequiresServiceDelay = obj.requiresServiceDelay;
                            this.currentCourtSitting.serviceTypeHideDistributionList = obj.hideDistributionList;
                        }
                    }
                    var i;
                    if (previousCourtSitting.assignedJudgeIds != null) {
                        this.initialCourtJudges = [];
                        for (i = 0; i < previousCourtSitting.assignedJudgeIds.length; i++) {
                            this.initialCourtJudges.push({
                                courtJudgeId: previousCourtSitting.assignedJudgeIds[i],
                                displayName: previousCourtSitting.assignedJudgeDisplayNames[i]
                            })
                        }
                    }
                    this.initialDeliveryContacts = previousCourtSitting.allDeliveryContactDetails;
                    this.initialCcDeliveryContacts = previousCourtSitting.allDeliveryContactDetails;
                    this.initialBccDeliveryContacts = previousCourtSitting.allDeliveryContactDetails;
                }
                this.newSittingsDateRange = null;
                this.addRange = true;
                this.currentErrors = [];
                this.editCourtSittingDialogShown = true;
                Vue.nextTick().then(() => {
                    this.$refs.multipleSittingDatesValidationProvider.reset();
                });

            },
            editCourtSitting: function (courtSitting) {
                this.editSittingAllowed = true;
                this.currentCourtSitting = {
                    courtSittingId: courtSitting.courtSittingId,
                    courtOrderId: courtSitting.courtOrderId,
                    courtSittingMatterTypeId: courtSitting.courtSittingMatterTypeId,
                    courtRoomId: courtSitting.courtRoomId,
                    assignedJudgeIds: courtSitting.assignedJudgeIds,
                    sittingDayCode: courtSitting.sittingDayCode,
                    sittingDate: this.formatDateTime(courtSitting.sittingDate, 'DD/MM/YYYY', true, true),
                    startTime: this.formatDateTime(courtSitting.startTime, 'HH:mm:ss', true, true),
                    endTime: this.formatDateTime(courtSitting.endTime, 'HH:mm:ss', true, true),
                    clientReference: courtSitting.clientReference,
                    serviceTypeId: courtSitting.serviceTypeId,
                    serviceDelayId: courtSitting.serviceDelayId,
                    calculatedDueDateTimeUTC: this.parseDateTime(courtSitting.calculatedDueDateTimeUTC),
                    manuallyEnteredDueDateTimeUTC: courtSitting.manuallyEnteredDueDateTimeUTC,
                    modeOfSitting: courtSitting.modeOfSittingOptionsList ? courtSitting.modeOfSittingOptionsList[0] : null,
                    restricted: courtSitting.restricted,
                    mandatoryRequest: courtSitting.mandatoryRequest,
                    audioQuality: courtSitting.audioQualityOptionsList ? courtSitting.audioQualityOptionsList[0] : null,
                    deliveryContactIds: courtSitting.deliveryContactIds,
                    deliveryContactCcIds: courtSitting.deliveryContactCcIds,
                    deliveryContactBccIds: courtSitting.deliveryContactBccIds,
                };
                this.loadRelatedOrders();
                if (this.currentCourtSitting.serviceTypeId != null) {
                    var obj = this.currentSelectableServiceTypes.find(serviceType => serviceType.serviceTypeId === this.currentCourtSitting.serviceTypeId);

                    if (obj != null) {
                        this.currentCourtSitting.serviceTypeRequiresServiceDelay = obj.requiresServiceDelay;
                        this.currentCourtSitting.serviceTypeHideDistributionList = obj.hideDistributionList;
                    }
                    else {
                        this.currentCourtSitting.serviceTypeId = null;
                        this.currentCourtSitting.serviceDelayId = null;
                        this.currentCourtSitting.serviceTypeRequiresServiceDelay = false;
                        this.currentCourtSitting.serviceTypeHideDistributionList = false;
                    }
                }
                if (courtSitting.courtRoomId != null) this.initialCourtRooms = [{ id: courtSitting.courtRoomId, text: courtSitting.courtLocationRoomName }];
                var i;
                if (courtSitting.assignedJudgeIds != null) {
                    this.initialCourtJudges = [];
                    for (i = 0; i < courtSitting.assignedJudgeIds.length; i++) {
                        this.initialCourtJudges.push({
                            courtJudgeId: courtSitting.assignedJudgeIds[i],
                            displayName: courtSitting.assignedJudgeDisplayNames[i]
                        })
                    }
                }
                this.initialDeliveryContacts = courtSitting.allDeliveryContactDetails;
                this.initialCcDeliveryContacts = courtSitting.allDeliveryContactDetails;
                this.initialBccDeliveryContacts = courtSitting.allDeliveryContactDetails;
                this.addRange = false;
                this.currentErrors = [];
                this.editCourtSittingDialogShown = true;
            },
            excludeCourtSitting: function (courtSitting) {
                courtSitting.included = false;
                if (this.currentCourtSittingOrderAssignments != null) {
                    var entryIndex = this.currentCourtSittingOrderAssignments.findIndex(element => element.courtSittingId == courtSitting.courtSittingId);
                    if (entryIndex >= 0) {
                        this.currentCourtSittingOrderAssignments.splice(entryIndex, 1);
                        this.$emit('change', this.currentCourtSittingOrderAssignments);
                    }
                }
            },
            includeCourtSitting: function (
                courtSitting,
                newClientReference,
                newServiceTypeId,
                newServiceDelayId,
                newDeliveryContacts,
                newDeliveryContactIds,
                newDeliveryContactCcIds,
                newDeliveryContactBccIds) {

                if ((newClientReference == null) && courtSitting.clientReference) newClientReference = courtSitting.clientReference;
                if ((newServiceTypeId == null) && courtSitting.serviceTypeId) newServiceTypeId = courtSitting.serviceTypeId;
                if ((newServiceDelayId == null) && courtSitting.serviceDelayId) newServiceDelayId = courtSitting.serviceDelayId;
                if ((newDeliveryContacts == null) && courtSitting.allDeliveryContactDetails) newDeliveryContacts = courtSitting.allDeliveryContactDetails;
                if ((newDeliveryContactIds == null) && courtSitting.deliveryContactIds) newDeliveryContactIds = courtSitting.deliveryContactIds;
                if ((newDeliveryContactCcIds == null) && courtSitting.deliveryContactCcIds) newDeliveryContactCcIds = courtSitting.deliveryContactCcIds;
                if ((newDeliveryContactBccIds == null) && courtSitting.deliveryContactBccIds) newDeliveryContactBccIds = courtSitting.deliveryContactBccIds;

                //@jim - you will have to put the code that works out service type, delay and delivery lists from a previous sitting here
                //first check whether the newServiceTypeId == null - only then the code is relevant, otherwise there are already valid settings
                //then find the index of the passed in courtSitting in the this.courtSittings array (like you did further below)
                //from that index - if it is greater than 0 - go back in the list of this.courtSittings and find the entry that has the included=true set
                //that is the previous sitting that is included in the order (of course always check to stop the beginning of the array)
                //if you find that entry, then use that entries props like you did below - BUT also update the newServiceTypeId, newServiceDelayId, newDeliveryContacts, newDeliveryContactIds, newDeliveryContactCcIds, newDeliveryContactBccIds
                if (newServiceTypeId == null) {
                    var sittingIndex = this.courtSittings.findIndex(element => element.courtSittingId == courtSitting.courtSittingId);
                    console.log('sittingIndex: ' + sittingIndex);
                    if (sittingIndex > 0) {
                        var checkIndex = sittingIndex--;

                        while (checkIndex >= 0) {
                            var previousSitting = this.courtSittings[checkIndex];

                            if (previousSitting && previousSitting.included) {
                                newServiceTypeId = previousSitting.serviceTypeId;
                                newServiceDelayId = previousSitting.serviceDelayId;
                                newDeliveryContacts = previousSitting.allDeliveryContactDetails;
                                newDeliveryContactIds = previousSitting.deliveryContactIds;
                                newDeliveryContactCcIds = previousSitting.deliveryContactCcIds;
                                newDeliveryContactBccIds = previousSitting.deliveryContactBccIds;

                                courtSitting.serviceTypeId = previousSitting.serviceTypeId;
                                courtSitting.serviceTypeName = previousSitting.serviceTypeName;
                                courtSitting.serviceDelayId = previousSitting.serviceDelayId;
                                courtSitting.serviceDelayName = previousSitting.serviceDelayName;
                                courtSitting.allDeliveryContactDetails = previousSitting.allDeliveryContactDetails;
                                courtSitting.deliveryContactIds = previousSitting.deliveryContactIds;
                                courtSitting.deliveryContactCcIds = previousSitting.deliveryContactCcIds;
                                courtSitting.deliveryContactBccIds = previousSitting.deliveryContactBccIds;
                                courtSitting.deliveryContactDisplayNames = previousSitting.deliveryContactDisplayNames;
                                checkIndex = -1;
                            }
                            else {
                                checkIndex--;
                            }
                        }
                    }
                }

                courtSitting.included = true;
                var serviceTypeRequiresServiceDelay = false;
                var serviceTypeHideDistributionList = false;
                if (newServiceTypeId != null) {
                    var obj = this.currentSelectableServiceTypes.find(serviceType => serviceType.serviceTypeId === newServiceTypeId);
                    if (obj != null) {
                        serviceTypeRequiresServiceDelay = obj.requiresServiceDelay;
                        serviceTypeHideDistributionList = obj.hideDistributionList;
                    }
                }
                if (this.currentCourtSittingOrderAssignments == null) {
                    this.currentCourtSittingOrderAssignments = [{
                        courtSittingId: courtSitting.courtSittingId,
                        courtOrderId: null,
                        sittingDate: courtSitting.sittingDate,
                        sittingDayCode: courtSitting.sittingDayCode,
                        sittingDayDateTimeDisplayText: courtSitting.sittingDayDateTimeDisplayText,
                        clientReference: newClientReference,
                        serviceTypeId: newServiceTypeId,
                        serviceTypeRequiresServiceDelay: serviceTypeRequiresServiceDelay,
                        serviceTypeHideDistributionList: serviceTypeHideDistributionList,
                        serviceDelayId: newServiceDelayId,
                        allDeliveryContactDetails: newDeliveryContacts,
                        deliveryContactIds: newDeliveryContactIds,
                        deliveryContactCcIds: newDeliveryContactCcIds,
                        deliveryContactBccIds: newDeliveryContactBccIds
                    }];
                } else {
                    var entryIndex = this.currentCourtSittingOrderAssignments.findIndex(element => element.courtSittingId == courtSitting.courtSittingId);
                    if (entryIndex < 0) {
                        this.currentCourtSittingOrderAssignments.push({
                            courtSittingId: courtSitting.courtSittingId,
                            courtOrderId: null,
                            sittingDate: courtSitting.sittingDate,
                            sittingDayCode: courtSitting.sittingDayCode,
                            sittingDayDateTimeDisplayText: courtSitting.sittingDayDateTimeDisplayText,
                            clientReference: newClientReference,
                            serviceTypeId: newServiceTypeId,
                            serviceTypeRequiresServiceDelay: serviceTypeRequiresServiceDelay,
                            serviceTypeHideDistributionList: serviceTypeHideDistributionList,
                            serviceDelayId: newServiceDelayId,
                            allDeliveryContactDetails: newDeliveryContacts,
                            deliveryContactIds: newDeliveryContactIds,
                            deliveryContactCcIds: newDeliveryContactCcIds,
                            deliveryContactBccIds: newDeliveryContactBccIds
                        });
                    }
                }
                this.$emit('change', this.currentCourtSittingOrderAssignments);
            },
            removeCourtSitting: function (courtSitting) {
                this.currentErrors = [];

                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to remove the sitting <strong>" + courtSitting.sittingDayDateTimeDisplayText + "</strong>?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Remove Sitting',
                        size: 'md',
                        okVariant: 'danger',
                        okTitle: 'Yes, Remove Sitting',
                        cancelTitle: 'No, Keep Sitting',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            axios
                                .delete(this.apiBaseUrl + '/courtsitting/' + courtSitting.courtSittingId)
                                .then(() => {
                                    this.reloadCourtSittings();
                                })
                                .catch(ex => {
                                    this.currentErrors = this.handleAxiosException(ex);
                                })
                                .finally(() => {
                                    if (this.currentErrors.length != 0) {
                                        this.showErrorsToast(this.currentErrors);
                                    }
                                });
                        }
                    });
            },
            findCourtRooms: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.apiBaseUrl + '/courtroom/bycourtregistry/' + this.courtRegistryId + '/find';
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.courtRoomId;
                                obj.text = obj.courtLocationRoomName;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            findJudges: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.$urlHelper.getApiUrl('courtjudge', 'bycourtregistry', [this.courtRegistryId, 'find']);
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.courtJudgeId;
                                obj.text = obj.displayName;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            createOrUpdateCourtSitting: function () {
                var data = {
                    courtMatterId: this.courtMatterId,
                    ...this.currentCourtSitting
                };
                if (this.currentCourtSitting.courtSittingId != null) {
                    data.sittingDate = this.parseDateTime(this.currentCourtSitting.sittingDate, 'DD/MM/YYYY');
                }
                else if (this.currentCourtSitting.sittingDate != null) {
                    //the input from the UI control has a time of 00:00:00 but the local timezone, to get rid of the timezone we have to
                    //convert the date to a string first and then create our own date/time object from it
                    data.sittingDate = this.parseDateTime(this.formatDateTime(this.currentCourtSitting.sittingDate, 'DD/MM/YYYY'), 'DD/MM/YYYY');
                }
                if (this.currentCourtSitting.startTime != null) {
                    data.startTime = this.parseDateTime(this.currentCourtSitting.startTime, 'HH:mm:ss', true, true);
                }
                if (this.currentCourtSitting.endTime != null) {
                    data.endTime = this.parseDateTime(this.currentCourtSitting.endTime, 'HH:mm:ss', true, true);
                }
                var newClientReference = null;
                var newServiceTypeId = null;
                var newServiceDelayId = null;
                var newDeliveryContacts = null;
                var newDeliveryContactIds = null;
                var newDeliveryContactCcIds = null;
                var newDeliveryContactBccIds = null;
                var selectedContacts = null;
                if (data.courtSittingId == null) {
                    delete data.courtSittingId;
                    newClientReference = this.currentCourtSitting.clientReference;
                    newServiceTypeId = this.currentCourtSitting.serviceTypeId;
                    newServiceDelayId = this.currentCourtSitting.serviceDelayId;
                    if (!this.hideDistributionList && !this.currentCourtSitting.serviceTypeHideDistributionList) {
                        newDeliveryContacts = [];
                        newDeliveryContactIds = this.currentCourtSitting.deliveryContactIds;
                        selectedContacts = this.$refs.deliveryContactSelect.getSelectedContacts();
                        if (selectedContacts != null) {
                            selectedContacts.forEach(contact => {
                                if (newDeliveryContacts.findIndex(e => e.contactId == contact.contactId) < 0) {
                                    newDeliveryContacts.push(contact);
                                }
                            });
                        }
                        newDeliveryContactCcIds = this.currentCourtSitting.deliveryContactCcIds;
                        selectedContacts = this.$refs.deliveryContactCcSelect.getSelectedContacts();
                        if (selectedContacts != null) {
                            selectedContacts.forEach(contact => {
                                if (newDeliveryContacts.findIndex(e => e.contactId == contact.contactId) < 0) {
                                    newDeliveryContacts.push(contact);
                                }
                            });
                        }
                        newDeliveryContactBccIds = this.currentCourtSitting.deliveryContactBccIds;
                        selectedContacts = this.$refs.deliveryContactBccSelect.getSelectedContacts()
                        if (selectedContacts != null) {
                            selectedContacts.forEach(contact => {
                                if (newDeliveryContacts.findIndex(e => e.contactId == contact.contactId) < 0) {
                                    newDeliveryContacts.push(contact);
                                }
                            });
                        }
                    }
                } else if (this.currentCourtSittingOrderAssignments != null) {

                    var courtSittingOrderAssignment = this.currentCourtSittingOrderAssignments.find(element => element.courtSittingId == this.currentCourtSitting.courtSittingId);
                    if (courtSittingOrderAssignment != null) {
                        courtSittingOrderAssignment.manuallyEnteredDueDateTimeUTC = this.currentCourtSitting.manuallyEnteredDueDateTimeUTC;
                        courtSittingOrderAssignment.clientReference = this.currentCourtSitting.clientReference;
                        courtSittingOrderAssignment.serviceTypeId = this.currentCourtSitting.serviceTypeId;
                        courtSittingOrderAssignment.serviceDelayId = this.currentCourtSitting.serviceDelayId;
                        courtSittingOrderAssignment.serviceTypeRequiresServiceDelay = this.currentCourtSitting.serviceTypeRequiresServiceDelay;
                        courtSittingOrderAssignment.serviceTypeHideDistributionList = this.currentCourtSitting.serviceTypeHideDistributionList;

                        if (!this.hideDistributionList && !courtSittingOrderAssignment.serviceTypeHideDistributionList) {
                            courtSittingOrderAssignment.allDeliveryContactDetails = [];
                            courtSittingOrderAssignment.deliveryContactIds = this.currentCourtSitting.deliveryContactIds;
                            selectedContacts = this.$refs.deliveryContactSelect.getSelectedContacts();
                            if (selectedContacts != null) {
                                selectedContacts.forEach(contact => {
                                    if (courtSittingOrderAssignment.allDeliveryContactDetails.findIndex(e => e.contactId == contact.contactId) < 0) {
                                        courtSittingOrderAssignment.allDeliveryContactDetails.push(contact);
                                    }
                                });
                            }
                            courtSittingOrderAssignment.deliveryContactCcIds = this.currentCourtSitting.deliveryContactCcIds;
                            selectedContacts = this.$refs.deliveryContactCcSelect.getSelectedContacts();
                            if (selectedContacts != null) {
                                selectedContacts.forEach(contact => {
                                    if (courtSittingOrderAssignment.allDeliveryContactDetails.findIndex(e => e.contactId == contact.contactId) < 0) {
                                        courtSittingOrderAssignment.allDeliveryContactDetails.push(contact);
                                    }
                                });
                            }
                            courtSittingOrderAssignment.deliveryContactBccIds = this.currentCourtSitting.deliveryContactBccIds;
                            selectedContacts = this.$refs.deliveryContactBccSelect.getSelectedContacts();
                            if (selectedContacts != null) {
                                selectedContacts.forEach(contact => {
                                    if (courtSittingOrderAssignment.allDeliveryContactDetails.findIndex(e => e.contactId == contact.contactId) < 0) {
                                        courtSittingOrderAssignment.allDeliveryContactDetails.push(contact);
                                    }
                                });
                            }
                        }
                        else {
                            courtSittingOrderAssignment.allDeliveryContactDetails = null;
                            courtSittingOrderAssignment.deliveryContactIds = null;
                            courtSittingOrderAssignment.deliveryContactCcIds = null;
                            courtSittingOrderAssignment.deliveryContactBccIds = null;
                        }
                        this.$emit('change', this.currentCourtSittingOrderAssignments);
                    }
                }

                if (this.editSittingAllowed) {
                    if (this.addRange) {
                        data = {
                            courtSitting: data,
                            //the input from the UI control has a time of 00:00:00 but the local timezone, to get rid of the timezone we have to
                            //convert the date to a string first and then create our own date/time object from it
                            startDate: this.parseDateTime(this.formatDateTime(this.newSittingsDateRange[0], 'DD/MM/YYYY'), 'DD/MM/YYYY'),
                            endDate: this.parseDateTime(this.formatDateTime(this.newSittingsDateRange[1], 'DD/MM/YYYY'), 'DD/MM/YYYY'),
                            excludeWeekends: this.excludeWeekends,
                        }
                        data.courtSitting.sittingDate = data.startDate;
                    }
                    this.currentErrors = [];
                    var url = this.apiBaseUrl + '/courtsitting';
                    if (this.addRange)
                        url = url + '/range';
                    axios
                        .request(
                            {
                                method: (this.currentCourtSitting.courtSittingId == null) ? 'post' : 'put',
                                url: url,
                                data: JSON.stringify(data),
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            })
                        .then(response => {
                            if (response.data instanceof Array) {
                                var newSittings = response.data;

                                newSittings.forEach(newSitting => {
                                    this.includeCourtSitting(
                                        newSitting,
                                        newClientReference,
                                        newServiceTypeId,
                                        newServiceDelayId,
                                        newDeliveryContacts,
                                        newDeliveryContactIds,
                                        newDeliveryContactCcIds,
                                        newDeliveryContactBccIds);
                                });
                            } else {
                                var newSitting = response.data;
                                this.includeCourtSitting(
                                    newSitting,
                                    newClientReference,
                                    newServiceTypeId,
                                    newServiceDelayId,
                                    newDeliveryContacts,
                                    newDeliveryContactIds,
                                    newDeliveryContactCcIds,
                                    newDeliveryContactBccIds);
                            }
                            this.reloadCourtSittings();
                        })
                        .catch(ex => {
                            this.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.currentErrors.length == 0) {
                                this.addRange = false;
                                this.editCourtSittingDialogShown = false;
                                if (this.addMoreRequestedSittings) this.addRequestedCourtSittings();
                            }
                        });
                } else {
                    this.includeCourtSitting(
                        this.currentCourtSitting,
                        newClientReference,
                        newServiceTypeId,
                        newServiceDelayId,
                        newDeliveryContacts,
                        newDeliveryContactIds,
                        newDeliveryContactCcIds,
                        newDeliveryContactBccIds);
                    this.reloadCourtSittings();
                    this.addRange = false;
                    this.editCourtSittingDialogShown = false;
                }
            },
            serviceTypeSelected: function (serviceType) {
                if (serviceType != null) {
                    this.currentCourtSitting.serviceTypeRequiresServiceDelay = serviceType.requiresServiceDelay;
                    if (!this.currentCourtSitting.serviceTypeRequiresServiceDelay) {
                        this.currentCourtSitting.serviceDelayId = null;
                    }
                    this.currentCourtSitting.serviceTypeHideDistributionList = serviceType.hideDistributionList;
                }
                else {
                    this.currentCourtSitting.serviceTypeRequiresServiceDelay = false;
                    this.currentCourtSitting.serviceTypeHideDistributionList = false;
                    this.currentCourtSitting.serviceDelayId = null;
                }

                this.showSelectDelayId = this.showSelectServiceDelay;
            },
            onManualDueDateChange: function () {
                //Check if manual due date is a public holiday

                if (!this.currentCourtSitting.manuallyEnteredDueDateTimeUTC) {
                    this.displayDueWarning = false;
                    return;
                }

                var url = this.apiBaseUrl + '/publicholiday/ispublicholiday';

                var data = {
                    date: this.formatDateTime(this.currentCourtSitting.manuallyEnteredDueDateTimeUTC, "yyyy-MM-DD", true, false),
                    courtRegistryId: this.courtRegistryId,
                    interviewOrganisationId: null,
                };

                var isPublicHoliday = false;

                axios
                    .request(
                        {
                            method: 'post',
                            url: url,
                            data: JSON.stringify(data),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(response => {
                        isPublicHoliday = response.data;
                        this.displayDueWarning = isPublicHoliday;
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    });

                this.displayDueWarning = isPublicHoliday;
            },
            modalShown: function () {
                console.log('refresh');
                Vue.nextTick().then(() => {
                    this.$refs.courtSittingAssignedJudgeIds.refresh();
                });
            }
        },
        mounted: function () {
            this.reloadCourtSittings(this.currentCourtSittingOrderAssignments == null);

        }
    }
</script>